import { useState } from "react";
import { Icon } from "../../components/Icon";
import { Badge, Btn, Card, Empty, Field, PageHead, Skeleton } from "../../components/kit";
import { fmt } from "../../data/mock";
import { buyCredits, openBillingPortal, startCheckout, useMe, type Me } from "../../lib/api";
import { useUsage, useBillingInfo, type UsageReport, type BillingInfo } from "../../lib/hooks";

/* Credit top-up sizes (credits the user buys). Autumn prices the purchase at
   checkout; the price here is indicative and should track your Autumn top-up. */
const TOPUP_PACKAGES = [
  { credits: 2000, price: 18 },
  { credits: 5000, price: 40, tag: "Best value" },
  { credits: 12000, price: 90 },
];
const FALLBACK_PRICE: Record<string, number> = { Free: 0, Pro: 29, Team: 49, Founder: 0 };

function GaugeW({ used, total, unlimited, size = 150 }: { used: number; total: number; unlimited: boolean; size?: number }) {
  const pct = unlimited ? 0 : Math.min(100, total > 0 ? (used / total) * 100 : 0);
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const warn = pct > 80;
  return (
    <div className={`gauge ${warn ? "warn" : ""}`} style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle className="gt" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" />
        <circle
          className="gp" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={c * (1 - (unlimited ? 1 : pct / 100))}
          transform={`rotate(-90 ${size / 2} ${size / 2})`} strokeLinecap="round"
        />
      </svg>
      <div className="gauge-l">
        <div className="gauge-v">{unlimited ? "∞" : fmt(Math.max(0, total - used))}</div>
        <div className="gauge-c">{unlimited ? "unlimited" : "credits left"}</div>
      </div>
    </div>
  );
}

export function BillingPage() {
  const me = useMe();
  const usage = useUsage();

  return (
    <div className="page">
      <PageHead title="Billing & usage" sub="Your plan, what you’ve used this cycle, and how it’s paid." />
      {!me || !usage ? <BillingSkeleton /> : <Loaded me={me} usage={usage} />}
    </div>
  );
}

function Loaded({ me, usage }: { me: NonNullable<Me>; usage: UsageReport }) {
  const [sel, setSel] = useState(1);
  const billing: BillingInfo | null = useBillingInfo();
  const isAdmin = me.role === "admin";

  const unlimited = me.credits?.unlimited === true;
  const allowance = me.credits?.included ?? 0;
  const used = usage.used;
  const pct = !unlimited && allowance > 0 ? Math.round((used / allowance) * 100) : 0;
  const perDay = Math.max(1, usage.stats.perDay);
  const projected = usage.stats.projected;
  const left = Math.max(0, allowance - used);
  const runOut = Math.max(1, Math.floor(left / perDay));
  const planName = `Orbit ${me.plan}`;

  // Real plan price from Autumn (matched by id), else a static fallback.
  const planFromAutumn = billing?.plans.find((p) => p.id.toLowerCase() === me.plan.toLowerCase());
  const planAmount = planFromAutumn?.amount ?? FALLBACK_PRICE[me.plan] ?? 0;
  const planInterval = planFromAutumn?.interval ?? "month";

  const nextTier: "pro" | "team" | null = me.plan === "Free" ? "pro" : me.plan === "Pro" ? "team" : null;
  const upgrade = () => (nextTier ? startCheckout(nextTier) : openBillingPortal()).catch(() => {});
  const topUp = () => buyCredits(TOPUP_PACKAGES[sel].credits).catch(() => {});

  const resetAt = me.credits?.resetAt ?? null;
  const daysLeft = resetAt ? Math.max(0, Math.ceil((resetAt - Date.now()) / 86_400_000)) : null;
  const renewLabel = resetAt ? new Date(resetAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
  const cycleNote = renewLabel ? `Resets ${renewLabel}${daysLeft != null ? ` · ${daysLeft}d left` : ""}` : "Monthly cycle";

  const sonnetCredits = usage.byModel.find((m) => m.model === "Sonnet")?.credits ?? 0;
  const opusCredits = usage.byModel.find((m) => m.model === "Opus")?.credits ?? 0;
  const splitTotal = Math.max(1, sonnetCredits + opusCredits);

  const activity = [
    { label: "Helper · in-the-moment", val: usage.byActivity.find((a) => a.label === "Helper")?.credits ?? 0, ic: "life-buoy", cls: "a-helper" },
    { label: "Coach · lessons", val: usage.byActivity.find((a) => a.label === "Coach")?.credits ?? 0, ic: "graduation-cap", cls: "a-coach" },
  ];

  const chartDays = usage.days.map((d) => ({ sonnet: d.sonnet, opus: d.opus }));
  const max = Math.max(1, ...chartDays.map((d) => d.sonnet + d.opus));
  const overTime = { avgDay: usage.stats.perDay, peakDay: usage.stats.peakDay, activeDays: usage.stats.activeDays, lessons: usage.stats.lessonsTaken };

  return (
    <>
      {!unlimited && allowance > 0 && (
        <div className="bill-alert">
          <span className="ba-ic"><Icon name="zap" size={18} strokeWidth={2.2} /></span>
          <div className="ba-text">
            <b>You’ve used {pct}% of this cycle.</b> At your current pace (~{fmt(perDay)}/day) you’ll run out in about {runOut} day
            {runOut > 1 ? "s" : ""}.{isAdmin ? " Top up or upgrade to stay uninterrupted." : " Ask your workspace admin to top up."}
          </div>
          {isAdmin && (
            <div className="ba-actions">
              {nextTier && <Btn variant="ghost" size="sm" icon="arrow-up-circle" onClick={upgrade}>Upgrade to {nextTier === "pro" ? "Pro" : "Team"}</Btn>}
              <Btn variant="live" size="sm" icon="zap" onClick={topUp}>Buy credits</Btn>
            </div>
          )}
        </div>
      )}

      <div className="bill-top">
        <Card title="This cycle" icon="gauge" iconColor="var(--live-dim)" action={<span className="muted-sm">{cycleNote}</span>}>
          <div className="usage-row">
            <GaugeW used={used} total={allowance} unlimited={unlimited} />
            <div className="usage-detail">
              <div className="ukv"><span>Used</span><b>{fmt(used)}</b></div>
              <div className="ukv"><span>Allowance</span><b>{unlimited ? "Unlimited" : fmt(allowance)}</b></div>
              <div className="ukv"><span>Daily average</span><b>{fmt(perDay)}/day</b></div>
              <div className="ukv warn"><span>Projected this cycle</span><b>{fmt(projected)}</b></div>
            </div>
          </div>
          {!unlimited && allowance > 0 && isAdmin && (
            <div className="forecast">
              <Icon name="trending-up" size={14} />
              <span>At this pace you’ll hit your allowance ~{runOut} day{runOut > 1 ? "s" : ""} before reset. <button className="link-live" onClick={() => openBillingPortal().catch(() => {})}>See options</button></span>
            </div>
          )}
        </Card>

        <Card title="Where credits go" icon="pie-chart" iconColor="var(--violet-500)">
          <div className="wc-sec">
            <div className="wc-h">By model</div>
            <div className="split-row"><span className="split-dot sonnet" /><span>Sonnet</span><b>{fmt(sonnetCredits)}</b><span className="wc-pct">{Math.round((sonnetCredits / splitTotal) * 100)}%</span></div>
            <div className="split-row"><span className="split-dot opus" /><span>Opus</span><b>{fmt(opusCredits)}</b><span className="wc-pct">{Math.round((opusCredits / splitTotal) * 100)}%</span></div>
            <div className="split-bar"><i className="sonnet" style={{ width: (sonnetCredits / splitTotal) * 100 + "%" }} /><i className="opus" style={{ width: (opusCredits / splitTotal) * 100 + "%" }} /></div>
          </div>
          <div className="wc-sec">
            <div className="wc-h">By activity</div>
            {activity.map((a, i) => (
              <div className="wc-act" key={i}>
                <span className={`wc-ic ${a.cls}`}><Icon name={a.ic} size={14} /></span>
                <span className="wc-label">{a.label}</span>
                <b>{fmt(a.val)}</b>
                <span className="wc-pct">{Math.round((a.val / Math.max(1, used)) * 100)}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card
        title="Usage over time" icon="bar-chart-3"
        action={<div className="legend"><span className="lg-i"><span className="split-dot sonnet" />Sonnet</span><span className="lg-i"><span className="split-dot opus" />Opus</span></div>}
      >
        <div className="chart tall">
          {chartDays.map((d, i) => {
            const total = d.sonnet + d.opus;
            return (
              <div className="cbx" key={i} style={{ height: Math.max(6, (total / max) * 100) + "%" }}>
                <span className="op" style={{ flexGrow: Math.max(0, d.opus) }} />
                <span className="so" style={{ flexGrow: Math.max(0, d.sonnet) }} />
              </div>
            );
          })}
        </div>
        <div className="chart-x"><span>2 weeks ago</span><span>Today</span></div>
        <div className="ustat-row">
          <div className="ustat"><div className="ustat-v">{fmt(overTime.avgDay)}</div><div className="ustat-l">Avg / day</div></div>
          <div className="ustat"><div className="ustat-v">{fmt(overTime.peakDay)}</div><div className="ustat-l">Peak day</div></div>
          <div className="ustat"><div className="ustat-v">{overTime.activeDays}</div><div className="ustat-l">Active days</div></div>
          <div className="ustat"><div className="ustat-v">{overTime.lessons}</div><div className="ustat-l">Lessons taken</div></div>
        </div>
      </Card>

      <div className="bill-top">
        <Card title="Plan" icon="badge-check" iconColor="var(--violet-500)">
          <div className="plan-card">
            <Badge tone="violet" icon="check">Current plan</Badge>
            <div className="plan-name">{planName}</div>
            <div className="plan-price">{planAmount > 0 ? <>${planAmount}<span>/{planInterval === "year" ? "yr" : "mo"}</span></> : <>{unlimited ? "Comp" : "Free"}</>}</div>
            <div className="muted-sm">{unlimited ? "Unlimited credits" : `${fmt(allowance)} credits / month`} + pay-as-you-go</div>
            {isAdmin && (
              <div className="plan-actions" style={{ marginTop: 14 }}>
                {nextTier
                  ? <Btn variant="live" icon="arrow-up-circle" onClick={upgrade}>Upgrade to {nextTier === "pro" ? "Pro" : "Team"}</Btn>
                  : <Btn variant="ghost" icon="arrow-left-right" onClick={() => openBillingPortal().catch(() => {})}>Change plan</Btn>}
                <Btn variant="ghost" icon="pause" onClick={() => openBillingPortal().catch(() => {})}>Manage</Btn>
              </div>
            )}
            {!isAdmin && <div className="muted-sm" style={{ marginTop: 12 }}>Billing is managed by your workspace admin.</div>}
          </div>
        </Card>

        <Card title="Buy credits" icon="zap" iconColor="var(--live-dim)" action={<span className="muted-sm">One-time top-up</span>}>
          <div className="topup-grid">
            {TOPUP_PACKAGES.map((o, i) => (
              <button className={`topup ${sel === i ? "sel" : ""}`} key={i} onClick={() => setSel(i)}>
                {o.tag && <span className="topup-tag">{o.tag}</span>}
                <span className="bradio" />
                <div className="topup-c">{fmt(o.credits)}</div>
                <div className="muted-sm">credits</div>
                <div className="topup-p">${o.price}</div>
              </button>
            ))}
          </div>
          <div className="buy-foot">
            <span className="muted-sm">{isAdmin ? "Secure checkout via Stripe" : "Ask your admin to top up"}</span>
            <Btn variant="live" icon="lock" onClick={topUp} disabled={!isAdmin}>Buy {fmt(TOPUP_PACKAGES[sel].credits)} credits</Btn>
          </div>
        </Card>
      </div>

      <div className="two-col">
        <Card title="Payment method" icon="credit-card" action={isAdmin ? <Btn variant="ghost" size="sm" icon="arrow-up-right" onClick={() => openBillingPortal().catch(() => {})}>Manage</Btn> : undefined}>
          <Field label="Billing email"><div className="field-static">{me.email}</div></Field>
          <div style={{ marginTop: 12 }}>
            {billing === null ? (
              <Skeleton h={56} r={10} />
            ) : billing.paymentMethod ? (
              <div className="pay-row">
                <span className="pay-ic"><Icon name="credit-card" size={18} /></span>
                <div style={{ flex: 1 }}>
                  <div className="pay-t">{billing.paymentMethod.brand} •••• {billing.paymentMethod.last4}</div>
                  {billing.paymentMethod.exp && <div className="muted-sm">Expires {billing.paymentMethod.exp}</div>}
                </div>
                <Badge tone="neutral">Default</Badge>
              </div>
            ) : (
              <Empty icon="credit-card">{billing.adminOnly ? "Billing details are visible to workspace admins." : "No card on file — add one at checkout or in the portal."}</Empty>
            )}
          </div>
        </Card>

        <Card
          title="Invoices" icon="receipt"
          count={billing?.invoices.length}
          action={isAdmin ? <Btn variant="ghost" size="sm" icon="arrow-up-right" onClick={() => openBillingPortal().catch(() => {})}>Portal</Btn> : undefined}
          pad={false}
        >
          {billing === null ? (
            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} h={20} />)}
            </div>
          ) : billing.invoices.length === 0 ? (
            <Empty icon="receipt">{billing.adminOnly ? "Invoices are visible to workspace admins." : "No invoices yet."}</Empty>
          ) : (
            <div className="inv-list">
              {billing.invoices.map((iv) => (
                <div className="inv" key={iv.id}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="inv-d">{new Date(iv.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
                    <div className="muted-sm">{iv.currency.toUpperCase()} · {iv.status}</div>
                  </div>
                  <Badge tone={iv.status === "paid" ? "live" : "neutral"}>{iv.status}</Badge>
                  <span className="inv-a">${(iv.total / 100).toFixed(2)}</span>
                  {iv.url
                    ? <a className="icon-mini" href={iv.url} target="_blank" rel="noreferrer"><Icon name="arrow-up-right" size={14} /></a>
                    : <span className="icon-mini" style={{ opacity: 0.4 }}><Icon name="arrow-up-right" size={14} /></span>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function BillingSkeleton() {
  return (
    <>
      <div className="bill-top">
        <Card title="This cycle" icon="gauge">
          <div className="usage-row">
            <Skeleton w={150} h={150} r={75} />
            <div className="usage-detail" style={{ flex: 1, display: "flex", flexDirection: "column", gap: 14 }}>
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} h={16} />)}
            </div>
          </div>
        </Card>
        <Card title="Where credits go" icon="pie-chart">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} h={16} />)}
          </div>
        </Card>
      </div>
      <Card title="Usage over time" icon="bar-chart-3">
        <Skeleton h={180} r={10} />
        <div className="ustat-row" style={{ marginTop: 14 }}>
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} w={60} h={32} />)}
        </div>
      </Card>
      <div className="bill-top">
        <Card title="Plan" icon="badge-check"><Skeleton h={200} r={12} /></Card>
        <Card title="Buy credits" icon="zap"><Skeleton h={200} r={12} /></Card>
      </div>
    </>
  );
}
